import { useState, useEffect } from "react";
import ArcanaDiagram from "./components/ArcanaChart";
import ArcanaTables from "./components/ArcanaTables";
import MatrixOfDestiny from "./components/MatrixOfDestinyChart";
import Login from "./Login";
import { supabase } from "./utils/supabase";
import { calculateMatrix } from "./utils/calculations";
import "./styles.css";

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [errors, setErrors] = useState({
    day: false,
    month: false,
    year: false,
    name: false,
  });

  const [day, setDay] = useState(null);
  const [month, setMonth] = useState(null);
  const [year, setYear] = useState(null);
  const [name, setName] = useState("");
  const [results, setResults] = useState(null);
  const [selectedMatrixType, setSelectedMatrixType] = useState("KarmicMatrix");
  const [previousDates, setPreviousDates] = useState([]);

  // ✅ AUTH HANDLING (FIXED)
  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // ✅ FETCH PREVIOUS SEARCHES (FIXED)
  useEffect(() => {
    if (!user) return;

    const fetchPreviousDates = async () => {
      const { data, error } = await supabase
        .from("search_history")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching previous dates:", error);
      } else {
        setPreviousDates(data || []);
      }
    };

    fetchPreviousDates();
  }, [user]);

  const handleMatrixTypeChange = (e) => {
    setSelectedMatrixType(e.target.value);
  };

  const calculate = async () => {
    const newErrors = {
      day: !day || day < 1 || day > 31,
      month: !month || month < 1 || month > 12,
      year: !year || year < 1000 || year > 9999,
      name: !name || name.trim() === "",
    };

    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    // save to DB if new
    const existing = previousDates.find(
      (d) =>
        d.day === day &&
        d.month === month &&
        d.year === year &&
        d.name === name
    );

    if (!existing) {
      const { data, error } = await supabase
        .from("search_history")
        .insert([
          {
            day,
            month,
            year,
            name,
            user_id: user.id,
          },
        ])
        .select();

      if (error) {
        console.error("Error saving search:", error);
      } else if (data && data.length > 0) {
        setPreviousDates([data[0], ...previousDates]);
      }
    }

    const calculatedResults = calculateMatrix(day, month, year);
    setResults(calculatedResults);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  // ✅ LOADING STATE
  if (loading) {
    return <div className="text-center mt-10">Loading...</div>;
  }

  // ✅ NOT LOGGED IN
  if (!user) {
    return <Login />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-10">
      <div className="max-w-4xl mx-auto px-4">

        {/* Logout */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleLogout}
            className="text-sm text-gray-600 hover:text-black"
          >
            Выйти
          </button>
        </div>

        {/* Input Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-10">
          <h1 className="text-3xl font-bold text-center mb-6">
            Калькулятор матрицы
          </h1>

          {/* Previous dates */}
          <select
            onChange={(e) => {
              const [selectedName, d, m, y] = e.target.value.split("|");
              setName(selectedName);
              setDay(parseInt(d));
              setMonth(parseInt(m));
              setYear(parseInt(y));
            }}
            className="mb-4 border rounded-xl px-4 py-3 w-full"
          >
            <option value="">Выберите дату</option>
            {previousDates.map((date) => (
              <option
                key={date.id}
                value={`${date.name}|${date.day}|${date.month}|${date.year}`}
              >
                ★ {date.name} – {date.day}.{date.month}.{date.year}
              </option>
            ))}
          </select>

          {/* Inputs */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <input
              placeholder="Имя"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border rounded-xl px-3 py-2"
            />
            <input
              type="number"
              placeholder="DD"
              value={day || ""}
              onChange={(e) => setDay(parseInt(e.target.value))}
              className="border rounded-xl px-3 py-2"
            />
            <input
              type="number"
              placeholder="MM"
              value={month || ""}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="border rounded-xl px-3 py-2"
            />
            <input
              type="number"
              placeholder="YYYY"
              value={year || ""}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="border rounded-xl px-3 py-2"
            />
          </div>

          <div className="flex gap-4 w-full">
            <select
              value={selectedMatrixType}
              onChange={handleMatrixTypeChange}
              className="border rounded-xl px-4 py-3 w-1/3"
            >
              <option value="KarmicMatrix">Кармическая матрица</option>
              <option value="MatrixOfDestiny">Матрица судьбы</option>
            </select>

            <button
              onClick={calculate}
              className="bg-blue-500 text-white px-4 py-3 rounded-xl w-1/3"
            >
              Рассчитать
            </button>
          </div>
        </div>

        {/* Results */}
        {results && (
          <>
            {selectedMatrixType === "KarmicMatrix" && (
              <>
                <ArcanaDiagram results={results} />
                <ArcanaTables results={results} />
              </>
            )}

            {selectedMatrixType === "MatrixOfDestiny" && (
              <MatrixOfDestiny day={day} month={month} year={year} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default App;